// Sugalarm — kiosk Wi-Fi agent hook (cycle 56).
//
// Modeled on `useVolumeAgent`. Owns reachability + Wi-Fi state on the
// loopback agent. Surface:
//   - `available` is true only when (a) the variant is "kiosk", (b) GET
//     /health succeeds, and (c) the agent reports `wifi_supported: true`.
//     False on any non-kiosk surface, on an unreachable agent, on an
//     agent that lacks `nmcli`, or while the feature is env-disabled.
//   - `status` is the latest `/wifi/status` payload (null while loading).
//   - `scan()` triggers a rescan and returns the network list.
//   - `connect({ssid, password})` POSTs to `/wifi/connect` and returns
//     the result body verbatim — including `ok:false, error:"..."` on
//     a clean auth failure, which the caller should render inline.
//   - `refresh()` re-pulls status and re-probes reachability.
//
// Variant-gated inside the hook (not at the call site) to keep Rules
// of Hooks happy — exactly like `useVolumeAgent`.

import { useCallback, useEffect, useState } from "react";

import * as wifiApi from "../api/wifi";
import type { ApiResult } from "../lib/types";
import type { FrameVariant } from "./useFrameVariant";

const REACHABILITY_POLL_MS = 15_000;
const STATUS_REFRESH_MS = 20_000;

export interface WifiAgentState {
  available: boolean;
  status: wifiApi.WifiStatus | null;
  scan: () => Promise<ApiResult<wifiApi.WifiNetwork[]>>;
  connect: (
    body: wifiApi.WifiConnectBody,
  ) => Promise<ApiResult<wifiApi.WifiConnectResult>>;
  refresh: () => Promise<void>;
}

export function useWifiAgent(variant: FrameVariant): WifiAgentState {
  const [available, setAvailable] = useState(false);
  const [status, setStatus] = useState<wifiApi.WifiStatus | null>(null);

  const refresh = useCallback(async () => {
    if (variant !== "kiosk") {
      setAvailable(false);
      setStatus(null);
      return;
    }
    const h = await wifiApi.getHealth();
    if (!h.ok || !h.data.wifi_supported) {
      setAvailable(false);
      setStatus(null);
      return;
    }
    setAvailable(true);
    const s = await wifiApi.getStatus();
    if (s.ok) setStatus(s.data);
  }, [variant]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Re-probe reachability every 15 s while the agent is unreachable
  // (matches the volume hook's cycle-34.12 self-heal). Once available,
  // the slower status-refresh interval below keeps `status` fresh.
  useEffect(() => {
    if (variant !== "kiosk") return;
    if (available) return;
    const id = window.setInterval(() => {
      void refresh();
    }, REACHABILITY_POLL_MS);
    return () => window.clearInterval(id);
  }, [variant, available, refresh]);

  useEffect(() => {
    if (!available) return;
    const id = window.setInterval(() => {
      void refresh();
    }, STATUS_REFRESH_MS);
    return () => window.clearInterval(id);
  }, [available, refresh]);

  const scan = useCallback(async () => {
    const r = await wifiApi.scan();
    if (!r.ok) {
      // Surface the loss of reachability so the panel hides itself
      // rather than holding stale state.
      if (r.error.kind === "network") setAvailable(false);
    }
    return r;
  }, []);

  const connect = useCallback(
    async (body: wifiApi.WifiConnectBody) => {
      const r = await wifiApi.connect(body);
      if (r.ok) {
        // Re-pull the agent's view of the world so the panel's
        // `status` row reflects the new SSID immediately.
        const s = await wifiApi.getStatus();
        if (s.ok) setStatus(s.data);
      }
      return r;
    },
    [],
  );

  return { available, status, scan, connect, refresh };
}
